import { useEffect, useMemo } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';

import { Button } from 'components/ui/button';
import { VisibilityField } from 'components/blocks/visibility-field';
import { WarningAlert } from 'components/blocks/warning-alert';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from 'components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from 'components/ui/form';
import { Input } from 'components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/ui/select';
import { Textarea } from 'components/ui/textarea';
import { useGetLlmModels } from 'features/chat/agent-chat-module/hooks/use-gpt-chat';
import type { IGetAgentModelResponse } from 'features/chat/agent-chat-module/services/gpt-chat.service';
import type { IFolder } from 'features/chat/agent-chat-module/types/kb.service.type';
import { useUpdateFolder } from 'features/chat/hooks/use-kb';
import { useIsOwner } from 'hooks/use-is-owner';
import { useToast } from 'hooks/use-toast';
import { CHUNKING_STRATEGIES } from '../constants';
import { getEmbeddingModelLabel, sortEmbeddingModels } from '../utils';
import { useTranslation } from 'react-i18next';
import {
  DEFAULT_RESOURCE_VISIBILITY,
  type ResourceVisibility,
} from 'types/visibility';

type ConfigureFolderForm = {
  name: string;
  description: string;
  model_name: string;
  provider: string;
  chunking_strategy: string;
  visibility: ResourceVisibility;
};

const getFolderVisibility = (folder?: IFolder): ResourceVisibility =>
  folder?.Visibility ?? folder?.visibility ?? DEFAULT_RESOURCE_VISIBILITY;

const getEmbeddingModels = (models?: IGetAgentModelResponse) =>
  sortEmbeddingModels((models || []).filter((model) => model.model_type === 'embedding'));

type ConfigureFolderModalProps = {
  projectKey: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  folder: IFolder;
};

export function ConfigureFolderModal({
  projectKey,
  open,
  onOpenChange,
  folder,
}: ConfigureFolderModalProps) {
  const { toast } = useToast();
  const { data: llmModels } = useGetLlmModels();
  const { mutateAsync: updateFolder, isPending } = useUpdateFolder();
  const { t } = useTranslation();
  const canEdit = useIsOwner(folder);
  const embeddingModels = useMemo(() => getEmbeddingModels(llmModels), [llmModels]);
  const configureFolderSchema = z.object({
    name: z
      .string()
      .trim()
      .min(1, t('NAME_REQUIRED') || 'Name is required')
      .max(50, t('NAME_MAX_50_CHARACTERS') || 'Name must be at most 50 characters'),
    description: z
      .string()
      .trim()
      .min(1, t('DESCRIPTION_REQUIRED') || 'Description is required')
      .max(200, t('DESCRIPTION_MAX_200_CHARACTERS') || 'Description must be at most 200 characters'),
    model_name: z.string().trim().min(1, t('EMBEDDING_MODEL_REQUIRED') || 'Embedding model is required'),
    provider: z.string().trim().min(1, t('PROVIDER_REQUIRED') || 'Provider is required'),
    chunking_strategy: z
      .string()
      .trim()
      .min(1, t('CHUNKING_STRATEGY_REQUIRED') || 'Chunking strategy is required'),
    visibility: z.enum(['private', 'public']),
  });

  const form = useForm<ConfigureFolderForm>({
    resolver: zodResolver(configureFolderSchema),
    defaultValues: {
      name: folder?.Name || '',
      description: folder?.Description || '',
      model_name: folder?.EmbeddingModelConfiguration?.Model || '',
      provider: folder?.EmbeddingModelConfiguration?.Provider || '',
      chunking_strategy: folder?.EmbeddingModelConfiguration?.ChunkingStrategy || 'semantic',
      visibility: getFolderVisibility(folder),
    },
    mode: 'onChange',
  });

  useEffect(() => {
    if (!open) {
      return;
    }

    form.reset({
      name: folder?.Name || '',
      description: folder?.Description || '',
      model_name: folder?.EmbeddingModelConfiguration?.Model || '',
      provider: folder?.EmbeddingModelConfiguration?.Provider || '',
      chunking_strategy: folder?.EmbeddingModelConfiguration?.ChunkingStrategy || 'semantic',
      visibility: getFolderVisibility(folder),
    });

    void form.trigger();
  }, [folder, form, open]);

  const onSubmit = async (values: ConfigureFolderForm) => {
    if (!canEdit) {
      return;
    }

    try {
      const fallbackSelectedModel = embeddingModels.find((model) => model.model_name === values.model_name);
      const provider = values.provider || fallbackSelectedModel?.provider || '';

      await updateFolder({
        project_key: projectKey,
        folder_id: folder._id,
        name: values.name,
        description: values.description,
        visibility: values.visibility,
        embedding_model_config: {
          Provider: provider,
          Model: values.model_name,
          ChunkingStrategy: values.chunking_strategy,
          IsProviderBlocks: folder.EmbeddingModelConfiguration?.IsProviderBlocks || true,
          ModelId: folder.EmbeddingModelConfiguration?.ModelId || null,
        },
      });

      toast({
        variant: 'success',
        title: t('FOLDER_UPDATED') || 'Folder updated',
        description:
          t('KNOWLEDGE_FOLDER_SETTINGS_SAVED_SUCCESSFULLY') ||
          'Knowledge folder settings were saved successfully.',
      });
      onOpenChange(false);
    } catch (error) {
      toast({
        title: t('FAILED_TO_UPDATE_FOLDER') || 'Failed to update folder',
        description: error instanceof Error ? error.message : t('PLEASE_TRY_AGAIN') || 'Please try again.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="flex max-h-[min(92dvh,100svh)] w-[calc(100vw-1.5rem)] max-w-2xl flex-col gap-0 overflow-hidden p-0 sm:max-h-[min(92dvh,820px)]">
        <DialogHeader className="shrink-0 px-5 pb-2 pt-5">
          <DialogTitle>
            {canEdit
              ? t('CONFIGURE_FOLDER') || 'Configure Folder'
              : t('VIEW_FOLDER') || 'View Folder'}
          </DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form
            onSubmit={(event) => {
              event.preventDefault();
              if (!canEdit) {
                return;
              }
              void form.handleSubmit(onSubmit)();
            }}
            className="flex min-h-0 flex-1 flex-col overflow-hidden"
          >
            <div className="min-h-0 max-h-[calc(min(92dvh,100svh)-10.5rem)] flex-1 overflow-y-auto overscroll-contain px-5 py-4 touch-pan-y [-webkit-overflow-scrolling:touch] sm:max-h-none">
              <fieldset
                disabled={!canEdit}
                className="m-0 min-w-0 space-y-5 border-0 p-0 pb-1 disabled:opacity-70"
              >
                {!canEdit ? (
                  <WarningAlert>
                    {t('YOU_ARE_NOT_THE_OWNER_OF_THIS_FOLDER') ||
                      'You are not the owner of this folder.'}
                  </WarningAlert>
                ) : null}

                <FormField
                  control={form.control}
                  name="name"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('FOLDER_NAME') || 'Folder Name'}</FormLabel>
                      <FormControl>
                        <Input placeholder={t('PRODUCT_DOCS') || 'Product docs'} {...field} />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>{t('DESCRIPTION') || 'Description'}</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder={t('WHAT_SHOULD_THIS_FOLDER_CONTAIN') || 'What should this folder contain?'}
                          height="110px"
                          rows={4}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="visibility"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <VisibilityField
                          value={field.value}
                          onValueChange={field.onChange}
                          idPrefix="configure-kb-folder-visibility"
                          disabled={isPending || !canEdit}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <div className="grid gap-4 md:grid-cols-2">
                  <FormField
                    control={form.control}
                    name="model_name"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('EMBEDDING_MODEL') || 'Embedding Model'}</FormLabel>
                        <Select
                          disabled
                          value={field.value}
                          onValueChange={(value) => {
                            const selectedModel = embeddingModels.find((model) => model.model_name === value);
                            field.onChange(value);
                            form.setValue('provider', selectedModel?.provider || '', {
                              shouldDirty: true,
                              shouldTouch: true,
                              shouldValidate: true,
                            });
                          }}
                        >
                          <FormControl>
                            <SelectTrigger aria-readonly>
                              <SelectValue placeholder={t('SELECT_A_MODEL') || 'Select a model'} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {embeddingModels.map((model) => (
                              <SelectItem key={`${model.provider}-${model.model_name}`} value={model.model_name}>
                                {getEmbeddingModelLabel(model, t)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />

                  <FormField
                    control={form.control}
                    name="chunking_strategy"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>{t('CHUNKING_STRATEGY') || 'Chunking Strategy'}</FormLabel>
                        <Select
                          disabled={!canEdit}
                          value={field.value}
                          onValueChange={field.onChange}
                        >
                          <FormControl>
                            <SelectTrigger>
                              <SelectValue placeholder={t('SELECT_A_STRATEGY') || 'Select a strategy'} />
                            </SelectTrigger>
                          </FormControl>
                          <SelectContent>
                            {CHUNKING_STRATEGIES.map((strategy) => (
                              <SelectItem key={strategy.value} value={strategy.value}>
                                {t(strategy.labelKey) || strategy.defaultLabel}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <FormMessage />
                      </FormItem>
                    )}
                  />
                </div>

                {canEdit ? (
                  <WarningAlert>
                    {t('CHANGING_EMBEDDING_MODEL_MAY_REPROCESS_EXISTING_KNOWLEDGE_DATA_AND_AFFECT_COSTS') ||
                      'Changing the embedding model may reprocess existing knowledge data and affect costs.'}
                  </WarningAlert>
                ) : null}
              </fieldset>
            </div>

            <DialogFooter className="shrink-0 border-t border-border/40 bg-background px-5 py-4">
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                {canEdit ? t('CANCEL') || 'Cancel' : t('CLOSE') || 'Close'}
              </Button>
              {canEdit ? (
                <Button
                  type="button"
                  loading={isPending}
                  disabled={isPending}
                  onClick={() => {
                    void form.handleSubmit(onSubmit)();
                  }}
                >
                  {t('SAVE') || 'Save'}
                </Button>
              ) : null}
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
